/**
 * lib/employerInviteBypass.js — Phase 0.7.2 invitee bypass marker.
 *
 * Purely client-side, session-scoped, user-id-tied marker that lets
 * a fresh invitee land in the employer workspace without first
 * completing the candidate-onboarding contract.
 *
 * Why a marker?
 *   The invitee has an `emp_account_members` row but no `profiles`
 *   row. The existing `ProtectedRoute requireProfile=true` gate
 *   would otherwise bounce them to `/onboarding` (a candidate flow).
 *   We set this marker on a successful invitation acceptance; it
 *   only unlocks `/employer/*` routes for the same user.id; it
 *   auto-clears on logout and on tab close.
 *
 * Defence-in-depth:
 *   - storage = sessionStorage (per-tab, evaporates on tab close)
 *   - the marker value IS the user.id — we only honour it when it
 *     matches the currently authenticated user
 *   - the marker is route-scoped — only honoured for /employer/*
 *     prefixes via the consumer (ProtectedRoute) logic
 *   - logout clears the marker via `clearEmployerInviteBypass()`
 */

const KEY = 'emp_invite_bypass_v1';

function _store() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch { return null; }
}

export function setEmployerInviteBypass(userId) {
  if (!userId) return;
  const s = _store();
  if (s) s.setItem(KEY, String(userId));
}

export function clearEmployerInviteBypass() {
  const s = _store();
  if (s) s.removeItem(KEY);
}

export function hasEmployerInviteBypassFor(userId) {
  if (!userId) return false;
  const s = _store();
  if (!s) return false;
  return s.getItem(KEY) === String(userId);
}
